import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { ViewOpts } from '@play-co/timestep-core/ui';
import gsap from 'gsap';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';

const assets = {
  bar: 'assets/ui/match2/common/bar_pinch_streak.png',
  inactive: 'assets/ui/match2/common/badge_pinch_streak.png',
  active: 'assets/ui/match2/common/badge_pinch_streak_active.png',
};

type Match2GenericStreakLossBarOpts = ViewOpts & {
  icon: string;
  items: number[];
  index: number;
};

export default class Match2GenericStreakLossBar extends FlexBoxView {
  private highlight?: FlexBoxImageScaleView;

  constructor(opts: Match2GenericStreakLossBarOpts) {
    super({
      flex: {
        width: 365,
        height: 80,
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      ...opts,
    });

    const { icon, items, index } = opts;

    this.createIcon(icon);
    this.createBar(items, index);
  }

  private createIcon(asset: string) {
    const size = 75;
    new FlexBoxImageScaleView({
      superview: this,
      image: asset,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      flex: {
        width: size,
        height: size,
      },
    });
  }

  private createBar(items: number[], current: number) {
    const width = 300;
    const wrapper = new FlexBoxView({
      superview: this,
      flex: {
        width,
        height: this.style.height,
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    });

    // Create bar
    new FlexBoxImageScaleView({
      superview: wrapper,
      image: assets.bar,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 5, right: 5 },
      },
      flex: {
        position: 'absolute',
        left: 30,
        right: 30,
        height: 11,
      },
    });

    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const active = index === current;
      const size = active ? 65 : 55;

      // Create badge for each item
      const badge = new FlexBoxImageScaleView({
        superview: wrapper,
        image: assets.inactive,
        flex: {
          width: size,
          height: size,
        },
      });

      // Overlay with active badge for animation, if applicable.
      if (active) {
        this.highlight = new FlexBoxImageScaleView({
          superview: badge,
          image: assets.active,
          width: size,
          height: size,
          flex: {
            position: 'absolute',
          },
        });
      }

      // Create item text
      new LangBitmapFontTextShadowView({
        superview: badge,
        centerAnchor: true,
        align: 'center',
        verticalAlign: 'center',
        size: active ? 27 : 22,
        font: 'Title',
        width: size - 5,
        height: size - 8,
        textShadowOffsetX: 0,
        textShadowOffsetY: 2,
        textShadowOpacity: 0.35,
        localeText: () => `x${item}`,
      });
    }
  }

  public flash() {
    if (!this.highlight) return;
    gsap.to(this.highlight.style, {
      opacity: 0,
      duration: 0.75,
      ease: 'quad.inOut',
      yoyo: true,
      repeat: -1,
    });
  }

  public unflash() {
    if (!this.highlight) return;
    gsap.killTweensOf(this.highlight.style);
  }
}
